import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, _resetSessionExpiredFlag, _markUserSessionStarted, _markUserSessionEnded } from "../lib/apiClient";
import useEvalMode from "../lib/useEvalMode";

// v3.99bc / v3.99bd — Synthetic identities for Field Access evaluators.
// Two variants:
//   EVALUATOR_USER   → projects a collector identity (role=customer)
//   EVALUATOR_VENDOR → projects an approved breeder identity (role=vendor)
// The choice is driven by which Field Access slug the evaluator used
// (collector vs breeder), stored in sessionStorage by useEvalMode.
// Both shapes mirror the real customer/vendor user model closely enough
// that all role-gated UI renders without special-casing every consumer.
// The `_eval: true` discriminator + the apiClient mutation interceptor
// guarantee no mutation actually reaches the backend.
const EVALUATOR_USER = Object.freeze({
  id: "evaluator",
  name: "Field Evaluator",
  email: "evaluator@thecannaforge.com",
  role: "customer",
  rank: "Collector",
  rank_label: "Collector",
  followed_breeders: [],
  saved_strains: [],
  _eval: true,
});

const EVALUATOR_VENDOR = Object.freeze({
  id: "evaluator-breeder",
  name: "Field Breeder",
  email: "breeder-evaluator@thecannaforge.com",
  role: "vendor",
  vendor_status: "approved",
  store_name: "Evaluator Genetics",
  store_slug: "evaluator-genetics",
  _eval: true,
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [error, setError] = useState("");
  const { active: evalMode, role: evalRole } = useEvalMode();

  const refresh = useCallback(async () => {
    try {
      // v3.99d.341o — If we're mid-Google-OAuth callback, the URL
      // fragment will contain ``session_id=`` and AuthCallback is
      // about to POST that to /api/auth/google/session. Skip the
      // /auth/session probe so we don't set the anonymous state
      // BEFORE the callback finishes.
      if (typeof window !== "undefined" &&
          window.location?.hash?.includes("session_id=")) {
        return;
      }
      // v3.99bx — Use /auth/session (soft, returns 200 + null for anon)
      // instead of /auth/me (hard 401) to avoid flooding the browser
      // console with red XHR errors on every anonymous page load.
      const { data } = await api.get("/auth/session");
      if (data && data.user) {
        setUser(data.user);
        _markUserSessionStarted();
      } else {
        setUser(false);
        _markUserSessionEnded();
      }
    } catch (e) {
      setUser(false);
      _markUserSessionEnded();
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      _resetSessionExpiredFlag();
      _markUserSessionStarted();
      setUser(data.user);
      return data.user;
    } catch (e) {
      const m = formatApiError(e);
      setError(m);
      throw new Error(m);
    }
  };

  const register = async (form) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", form);
      _resetSessionExpiredFlag();
      _markUserSessionStarted();
      setUser(data.user);
      return data.user;
    } catch (e) {
      const m = formatApiError(e);
      setError(m);
      throw new Error(m);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    _markUserSessionEnded();
    setUser(false);
  };

  // v3.99bc / v3.99bd — Project the synthetic evaluator into the auth
  // surface only when no real user is signed in. Real signed-in users
  // (even if they accidentally hit a Field Access slug) keep their own
  // identity. The role projected matches the slug they entered with.
  const syntheticEvaluator =
    evalRole === "vendor" ? EVALUATOR_VENDOR : EVALUATOR_USER;
  const effectiveUser =
    evalMode && (user === false || (user && user._eval))
      ? syntheticEvaluator
      : user;

  return (
    <AuthContext.Provider value={{
      user: effectiveUser,
      error,
      login,
      register,
      logout,
      refresh,
      // Expose for components that want to gate destructive UI without
      // re-reading sessionStorage themselves.
      isEvaluator: !!(effectiveUser && effectiveUser._eval),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
